import { useState, useEffect, useRef  } from "react";
import { Link } from "react-router-dom";
import { Scissors, Cat, Sparkles, BedDouble, ShoppingBag, HeartHandshake, Check, PawPrint, Phone, CalendarCheck } from "lucide-react";
import { businessConfig } from "@/config";

const SERVICES = [
  {
    id: "dog-grooming",
    label: "Dog Grooming",
    icon: Scissors,
    headline: "Professional dog grooming for every breed.",
    subhead: "Expert care. Happy dogs.",
    description:
      "From breed cuts to bath & tidy, we use gentle techniques, premium products, and lots of love.",
    checklist: [
      "Breed cuts, de-shedding & bath & tidy",
      "Nail trim, ear cleaning & anal gland care",
      "Puppy intro groom-ups",
      "Master-certified",
    ],
  },
  {
    id: "cat-grooming",
    label: "Cat Grooming",
    icon: Cat,
    headline: "Calm, careful grooming for cats of all temperaments.",
    subhead: "One of the few in Moose Jaw.",
    description:
      "Cats need specialist handling. Our team is trained and patient — your cat is in good hands.",
    checklist: [
      "Bath, blow-dry & brush-out",
      "Lion cuts & breed trims",
      "Nail trim & ear cleaning",
      "Cat specialist",
    ],
  },
  {
    id: "daycare",
    label: "Daycare",
    icon: Sparkles,
    headline: "A full day of play, supervised and safe.",
    subhead: "Drop-in friendly.",
    description:
      "Your dog gets socialization, exercise, and attention while you work. Pick them up tired and happy.",
    checklist: [
      "Supervised group play",
      "Rest periods built in",
      "Drop-in or regular schedule",
      "All sizes welcome",
    ],
  },
  {
    id: "boarding",
    label: "Boarding",
    icon: BedDouble,
    headline: "Safe, comfortable boarding for dogs and cats.",
    subhead: "Dogs & cats welcome.",
    description:
      "Go away knowing your pet is sleeping somewhere safe, warm, and cared for.",
    checklist: [
      "Dogs and cats both welcome",
      "Individual sleeping areas",
      "Daily updates on request",
      "Vaccinations required",
    ],
  },
  {
    id: "boutique",
    label: "Boutique",
    icon: ShoppingBag,
    headline: "Treats, toys, and goods for your pets.",
    subhead: "Curated with love.",
    description:
      "Browse our selection of treats, accessories, and gifts — handpicked for quality.",
    checklist: [
      "Premium treats and chews",
      "Accessories and toys",
      "Gift items for pet lovers",
      "In-store only",
    ],
  },
];

const RESCUE_TAB = {
  id: "rescue-tab",
  label: "Rescue",
  icon: HeartHandshake,
  scrollTo: "#rescue",
};

function ServicePanel({ service }) {
  const Icon = service.icon;
  const image = businessConfig.serviceImages?.[service.id];
  return (
    <div className="service-panel" id={service.id} data-testid={`service-panel-${service.id}`}>
      <div
        className={`service-panel__illustration ${image ? "service-panel__illustration--image" : ""}`}
        aria-hidden
      >
        {image ? (
          <img src={image} alt="" loading="lazy" data-testid={`service-image-${service.id}`} />
        ) : (
          <div className="service-panel__illustration-inner">
            <Icon className="w-10 h-10" style={{ color: "var(--pp-gold)" }} strokeWidth={1.4} />
            <span className="service-panel__illustration-caption">service illustration</span>
          </div>
        )}
      </div>
      <div className="service-panel__copy">
        <PawPrint className="w-5 h-5" style={{ color: "var(--pp-indigo)" }} strokeWidth={2} />
        <span className="overline" >{service.label}</span>
        <h3 className="service-panel__headline">{service.headline}</h3>
        <p className="service-panel__subhead">{service.subhead}</p>
        <span className="service-panel__divider" aria-hidden />
        <p className="service-panel__description">{service.description}</p>
        <ul className="service-panel__checklist">
          {service.checklist.map((c) => (
            <li key={c}>
              <Check className="w-4 h-4 flex-shrink-0" style={{ color: "var(--pp-indigo)" }} strokeWidth={2.5} />
              <span>{c}</span>
            </li>
          ))}
        </ul>
        <div className="service-panel__ctas">
          <a
            href="tel:3066910001"
            className="pp-btn-confirm"
            data-testid={`service-call-${service.id}`}
          >
            <Phone className="w-4 h-4" />
            Call for {service.label} · 306-691-0001
          </a>
          <Link
            to="/book"
            className="service-panel__book-link"
            data-testid={`service-book-${service.id}`}
          >
            <CalendarCheck className="w-4 h-4" />
            Book this online
          </Link>
        </div>
      </div>
    </div>
  );
}
export default function ServicesSection() {
  const [active, setActive] = useState(SERVICES[0].id);
  const tabsRef = useRef(null);
  const [pinned, setPinned] = useState(false);

  // Sync URL hash on tab switch (and initial mount).
  useEffect(() => {
    const sync = () => {
      const hash = window.location.hash.slice(1);
      const found = SERVICES.find((s) => s.id === hash);
      if (found) setActive(found.id);
    };
    sync();
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pin tab bar after it scrolls past the top, keep it until page bottom
useEffect(() => {
  const tabBar = tabsRef.current;
  if (!tabBar) return;

  const getHeaderHeight = () => {
    const header = document.querySelector('.landing-header');
    return header ? header.getBoundingClientRect().height : 57;
  };

  let timeout;
  const handleScroll = () => {
    if (!tabsRef.current) return;
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      const rect = tabsRef.current.getBoundingClientRect();
      const headerHeight = getHeaderHeight();
      setPinned((prev) => {
        if (!prev) return rect.top < headerHeight;
        return rect.bottom > headerHeight + 4 ? false : true;
      });
    }, 10);
  };

  window.addEventListener("scroll", handleScroll, { passive: true });
  return () => {
    window.removeEventListener("scroll", handleScroll);
    clearTimeout(timeout);
  };
}, []);
 const handleTab = (svc) => {
  if (svc.scrollTo) {
    const el = document.querySelector(svc.scrollTo);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    return;
  }
  setActive(svc.id);
  if (history.replaceState) history.replaceState(null, "", `#${svc.id}`);
  if (pinned) {
    const panel = document.querySelector(".services-tab-panel-wrap");
    if (panel) {
      const headerHeight = document.querySelector(".landing-header")?.getBoundingClientRect().height || 57;
      const tabBarHeight = tabsRef.current?.getBoundingClientRect().height || 60;
      const top = panel.getBoundingClientRect().top + window.scrollY - headerHeight - tabBarHeight - 8;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }
};
  const activeService = SERVICES.find((s) => s.id === active) || SERVICES[0];

  const tabList = (
    <>
      {[...SERVICES, RESCUE_TAB].map((svc) => {
        const Icon = svc.icon;
        const isActive = svc.id === active;
        const isRescue = svc.id === "rescue-tab";
        return (
          <button
            key={svc.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`services-tab ${isActive ? "is-active" : ""} ${isRescue ? "is-rescue" : ""}`}
            onClick={() => handleTab(svc)}
            data-testid={`services-tab-${svc.id}`}
          >
            <Icon className="w-6 h-6" style={{ color: isRescue ? "var(--pp-rescue)" : "var(--pp-indigo)" }} strokeWidth={1.6} />
            <span>{svc.label}</span>
          </button>
        );
      })}
    </>
  );

  return (
    <section className="services-section" id="services" data-testid="services-section">
      <div className="services-section__heading">
       <span className="overline" style={{ color: "var(--pp-gold)" }}>WHAT WE DO</span>
        <h2 className="section-h2">Our Services</h2>
        <p className="section-subhead">Choose a service to learn more.</p>
      </div>

      {/* Original tab strip — marks the position, becomes invisible when pinned */}
      <div
        ref={tabsRef}
        className="services-tabs"
        role="tablist"
        aria-label="Services"
        style={pinned ? { visibility: "hidden" } : undefined}
      >
        {tabList}
      </div>

      {/* Pinned floating copy — appears at top once original scrolls away */}
     
      {pinned && (
        <div
          style={{
            position: "fixed",
            top: `${document.querySelector('.landing-header')?.getBoundingClientRect().height || 57}px`,
            left: 0,
            right: 0,
            zIndex: 40,
            background: "#fff",
            borderBottom: "1px solid var(--pp-surface)",
            boxShadow: "0 4px 12px -4px rgba(42, 37, 64, 0.12)",
            padding: "0 28px",
          }}
        >
          <div
            className="services-tabs"
            role="tablist"
            aria-label="Services"
            style={{
              position: "static",
              maxWidth: "1280px",
              margin: "0 auto",
              boxShadow: "none",
              borderRadius: "var(--pp-radius)",
            }}
          >
            {tabList}
          </div>
        </div>
      )}

      {/* Desktop active panel */}
      <div className="services-tab-panel-wrap">
        <ServicePanel service={activeService} />
      </div>

      {/* Mobile accordion */}
      <div className="services-accordion">
        {SERVICES.map((svc) => (
          <details key={svc.id} className="services-accordion__item" id={`acc-${svc.id}`}>
            <summary>
              <span className="services-accordion__icon">
                <svc.icon className="w-5 h-5" style={{ color: "var(--pp-indigo)" }} strokeWidth={1.6} />
              </span>
              <span>{svc.label}</span>
              <span className="services-accordion__plus" aria-hidden>+</span>
            </summary>
            <div className="services-accordion__body">
              <ServicePanel service={svc} />
            </div>
          </details>
        ))}
        <a
          href="#rescue"
          className="services-accordion__item services-accordion__rescue"
          data-testid="services-accordion-rescue"
        >
          <span className="services-accordion__icon">
            <RESCUE_TAB.icon className="w-5 h-5" style={{ color: "var(--pp-rescue)" }} strokeWidth={1.6} />
          </span>
          <span>Rescue</span>
          <span className="services-accordion__arrow" aria-hidden>↓</span>
        </a>
      </div>
    </section>
  );
}
